import React, { useState, Suspense, useRef, useEffect } from 'react';
import { Engine, Scene, useBeforeRender, Model, useScene } from 'react-babylonjs';
import { Vector3, Color3, GlowLayer, PointerEventTypes, WebXRFeatureName, Tags, Ray, PhysicsImpostor, CannonJSPlugin, MeshBuilder } from '@babylonjs/core';
import { useLocation } from 'react-router-dom';
import '@babylonjs/loaders';
import { createVRMenu } from './VRMenu';
import * as CANNON from 'cannon-es';

const GALLERY_ITEMS = [
    { name: 'Sci-Fi Helmet', url: '/assets/models/model1.gltf', position: new Vector3(-8, 0.8, 3), scale: 0.8, rotation: Math.PI },
    { name: 'NINJA', url: '/assets/models/Ninja_5k_00001_.glb', position: new Vector3(-4, 0.8, 3), scale: 1.1, rotation: Math.PI },
    { name: 'Magic Potion', url: '/assets/models/model2.gltf', position: new Vector3(0, 0.8, 3), scale: 0.8, rotation: Math.PI },
    { name: 'Ancient Artifact', url: '/assets/models/model3.gltf', position: new Vector3(4, 0.8, 3), scale: 0.8, rotation: Math.PI },
    { name: 'VRai3D Logo', url: '/assets/models/vrai3d_logo_250k_00003_.glb', position: new Vector3(8, 0.8, 3), scale: 0.8, rotation: Math.PI },
];

const PortalCube = () => {
    const boxRef = useRef<any>(null);
    useBeforeRender((scene) => {
        if (boxRef.current) {
            const dt = scene.getEngine().getDeltaTime();
            boxRef.current.rotation.y += dt * 0.001;
            boxRef.current.rotation.x += dt * 0.0005;
        }
    });

    return (
        <box
            ref={boxRef}
            name="portal"
            size={1.5}
            position={new Vector3(0, 1.5, 7)}
            checkCollisions={true}
        >
            <pbrMaterial
                name="portal-mat"
                albedoColor={Color3.FromHexString("#00e6ff")}
                metallic={0.2}
                roughness={0.0}
                alpha={0.7}
                emissiveColor={Color3.FromHexString("#0033ff")}
                emissiveIntensity={1.2}
            />
        </box>
    );
};

const Pedestal = ({ position, label }: { position: Vector3; label: string }) => (
    <>
        <box
            name={`pedestal-${label}`}
            width={1.2}
            height={0.8}
            depth={1.2}
            position={new Vector3(position.x, 0.4, position.z)}
            checkCollisions={true}
            onCreated={(mesh) => {
                if (!mesh.physicsImpostor && mesh.getScene().getPhysicsEngine()) {
                    mesh.physicsImpostor = new PhysicsImpostor(
                        mesh,
                        PhysicsImpostor.BoxImpostor,
                        {
                            mass: 0,
                            restitution: 0, // No bounce = no tunneling
                            friction: 1.0
                        },
                        mesh.getScene()
                    );
                }
            }}
        >
            <pbrMaterial
                name={`pedestal-mat-${label}`}
                albedoColor={new Color3(0.05, 0.05, 0.08)}
                metallic={0.9}
                roughness={0.2}
                emissiveColor={new Color3(0.01, 0.01, 0.02)}
            />
        </box>
        <box
            name={`rim_light_${label}`} // No hyphen to avoid safety loop
            width={1.22}
            height={0.05}
            depth={1.22}
            position={new Vector3(position.x, 0.78, position.z)}
            isPickable={false}
        >
            <standardMaterial
                name={`pedestal-rim-mat-${label}`}
                emissiveColor={Color3.FromHexString("#00e6ff")}
                disableLighting={true}
            />
        </box>
    </>
);

const GalleryItem = ({ item }: { item: typeof GALLERY_ITEMS[0] }) => (
    <Suspense fallback={null}>
        <Model
            name={`gallery-${item.name}`}
            rootUrl={item.url}
            sceneFilename=""
            position={Vector3.Zero()} // Positioned via proxy
            rotation={new Vector3(0, item.rotation, 0)}
            scaling={new Vector3(item.scale, item.scale, item.scale)}
            onModelLoaded={(model: any) => {
                const scene = model.meshes[0].getScene();
                const root = model.meshes[0];
                if (root) {
                    // 1. Filter out internal colliders
                    model.meshes.forEach((mesh: any) => {
                        const meshName = mesh.name.toLowerCase();
                        if (meshName.includes('collision') || meshName.includes('collider') || meshName.includes('phys')) {
                            mesh.isVisible = false;
                            mesh.setEnabled(false);
                            mesh.checkCollisions = false;
                        }
                    });

                    root.computeWorldMatrix(true);
                    model.meshes.forEach((m: any) => m.computeWorldMatrix(true));

                    // 2. Manual Bounding Box for true visibility
                    let minWorldY = Infinity, maxWorldY = -Infinity;
                    let minWorldX = Infinity, maxWorldX = -Infinity;
                    let minWorldZ = Infinity, maxWorldZ = -Infinity;

                    model.meshes.forEach((mesh: any) => {
                        if (mesh.isEnabled() && mesh.isVisible && mesh.getBoundingInfo) {
                            const bounds = mesh.getBoundingInfo().boundingBox;
                            bounds.vectorsWorld.forEach((v: Vector3) => {
                                if (v.y < minWorldY) minWorldY = v.y;
                                if (v.y > maxWorldY) maxWorldY = v.y;
                                if (v.x < minWorldX) minWorldX = v.x;
                                if (v.x > maxWorldX) maxWorldX = v.x;
                                if (v.z < minWorldZ) minWorldZ = v.z;
                                if (v.z > maxWorldZ) maxWorldZ = v.z;
                            });
                        }
                    });

                    const visHeight = maxWorldY - minWorldY;
                    const visWidth = maxWorldX - minWorldX;
                    const visDepth = maxWorldZ - minWorldZ;

                    // 3. Create the Physics Proxy Shell (Invisible Box)
                    const proxyHeight = Math.max(visHeight, 0.2);
                    const proxy = MeshBuilder.CreateBox(`proxy-${item.name}`, {
                        width: Math.max(visWidth, 0.2),
                        height: proxyHeight,
                        depth: Math.max(visDepth, 0.2)
                    }, scene);

                    // Position proxy exactly at floor level (0cm)
                    const pedestalTop = item.position.y;
                    proxy.position = new Vector3(item.position.x, pedestalTop + (proxyHeight / 2), item.position.z);
                    proxy.isVisible = false;
                    proxy.checkCollisions = true;
                    proxy.isPickable = true;
                    Tags.AddTagsTo(proxy, "grabbable");

                    // 4. Parent character to Proxy
                    root.computeWorldMatrix(true, true);
                    const worldRootY = root.getAbsolutePosition().y;
                    const feetToRootOffset = worldRootY - minWorldY;

                    root.setParent(proxy);
                    root.position = new Vector3(0, -(proxyHeight / 2) + feetToRootOffset, 0);

                    // Re-tag visible meshes
                    model.meshes.forEach((mesh: any) => {
                        if (mesh.isEnabled() && mesh.isVisible) {
                            Tags.AddTagsTo(mesh, "grabbable");
                            mesh.isPickable = true;
                        }
                    });

                    proxy.metadata = {
                        itemName: item.name,
                        originalPosition: proxy.position.clone(),
                        dimensions: { w: visWidth, h: visHeight, d: visDepth }
                    };

                    // 5. Freeze-Frame Lifecycle: Start Static, then Wake Up
                    const wakeUp = () => {
                        if (!proxy || !scene.getPhysicsEngine() || (proxy as any)._isWaking) return;
                        (proxy as any)._isWaking = true;

                        if (proxy.physicsImpostor) {
                            proxy.physicsImpostor.dispose();
                        }

                        proxy.physicsImpostor = new PhysicsImpostor(
                            proxy,
                            PhysicsImpostor.BoxImpostor,
                            { mass: 1, restitution: 0, friction: 2.0 },
                            scene
                        );
                        if (proxy.physicsImpostor.physicsBody) {
                            proxy.physicsImpostor.physicsBody.angularDamping = 0.99;
                            proxy.physicsImpostor.physicsBody.linearDamping = 0.99;
                        }
                        console.log(`[Freeze-Frame] ${item.name} is now DYNAMIC.`);
                    };

                    // Initial Static Impostor (Mass 0)
                    proxy.physicsImpostor = new PhysicsImpostor(
                        proxy,
                        PhysicsImpostor.BoxImpostor,
                        { mass: 0, restitution: 0, friction: 2.0 },
                        scene
                    );

                    // Store wakeup on proxy so interaction can trigger it early
                    proxy.metadata.wakeUp = wakeUp;

                    // Wake up after 1.5s (allow engine to settle)
                    setTimeout(() => wakeUp(), 1500);

                    console.log(`[Freeze-Frame] ${item.name}: Static spawn at 0cm.`);
                }
            }}
        />
    </Suspense>
);

const Room = ({ roomUrl }: { roomUrl: string }) => (
    <Suspense fallback={null}>
        <Model
            name="room"
            rootUrl={roomUrl}
            sceneFilename=""
            onModelLoaded={(model: any) => {
                model.meshes.forEach((mesh: any) => {
                    mesh.checkCollisions = true;
                });
            }}
        />
        <PortalCube />
    </Suspense>
);

const SceneContent = ({ skies, skyIndex, cycleSkybox, modelUrl, vrMenuRef }: any) => {
    const scene = useScene();
    const currentRoom = modelUrl;
    const setupRun = useRef(false);
    const grabbedMeshRef = useRef<any>(null);
    const cycleSkyboxRef = useRef(cycleSkybox);

    useEffect(() => {
        cycleSkyboxRef.current = cycleSkybox;
    }, [cycleSkybox]);

    useEffect(() => {
        if (!scene || setupRun.current) return;
        setupRun.current = true;

        let xrInstance: any = null;
        let vrMenuInstance: any = null;

        const init = async () => {
            const gl = new GlowLayer("glow", scene);
            gl.intensity = 1.0;

            // Helper: reset all gallery items to their original positions
            const resetAllItems = () => {
                scene.meshes.forEach((mesh: any) => {
                    if (Tags.MatchesQuery(mesh, "grabbable") && mesh.name.startsWith("proxy-") && mesh.metadata?.originalPosition) {
                        try {
                            mesh.setParent(null);
                            if (mesh.physicsImpostor) {
                                mesh.physicsImpostor.setLinearVelocity(Vector3.Zero());
                                mesh.physicsImpostor.setAngularVelocity(Vector3.Zero());
                                mesh.physicsImpostor.dispose();
                                mesh.physicsImpostor = null;
                            }
                            mesh.position = mesh.metadata.originalPosition.clone();
                            mesh.rotationQuaternion = null;
                            (mesh as any)._isWaking = false;

                            // Re-apply Static first
                            mesh.physicsImpostor = new PhysicsImpostor(
                                mesh,
                                PhysicsImpostor.BoxImpostor,
                                { mass: 0, restitution: 0, friction: 2.0 },
                                scene
                            );

                            // Schedule wakeup
                            setTimeout(() => {
                                if (mesh.metadata?.wakeUp) mesh.metadata.wakeUp();
                            }, 1500);
                        } catch (e) { console.error("Error resetting proxy", e); }
                    }
                });
                grabbedMeshRef.current = null;
            };

            // Initialize physics engine with extreme fidelity
            const cannonPlugin = new CannonJSPlugin(true, 20, CANNON); // 20 substeps for high-precision
            scene.enablePhysics(new Vector3(0, -9.81, 0), cannonPlugin);
            console.log('Physics engine enabled with 20 substeps!');

            // Post-Physics Safety Check: Ensure ONLY pedestals and ground have colliders
            setTimeout(() => {
                scene.meshes.forEach((mesh: any) => {
                    const isBasePedestal = mesh.name.startsWith("pedestal-") && !mesh.name.includes("rim");
                    const isGroundOrWall = mesh.name === "ground" || mesh.name.startsWith("wall-");

                    if ((isBasePedestal || isGroundOrWall) && !mesh.physicsImpostor) {
                        mesh.physicsImpostor = new PhysicsImpostor(
                            mesh,
                            PhysicsImpostor.BoxImpostor,
                            {
                                mass: 0,
                                restitution: 0,
                                friction: 2.0
                            },
                            scene
                        );
                        console.log(`[Safety] Added base collider to ${mesh.name}`);
                    }
                });
            }, 1000);

            // Add static physics impostors to ground and walls
            // Using a short delay to ensure meshes are ready in the scene
            setTimeout(() => {
                // Ground physics
                const groundMeshForPhysics = scene.getMeshByName("ground");
                if (groundMeshForPhysics) {
                    groundMeshForPhysics.physicsImpostor = new PhysicsImpostor(
                        groundMeshForPhysics,
                        PhysicsImpostor.BoxImpostor,
                        { mass: 0, restitution: 0, friction: 2.0 },
                        scene
                    );
                }

                // Create invisible walls around the platform edge
                const wallHeight = 0.6; // ~2-3 bricks high
                const wallThickness = 0.3;
                const platformSize = 20;
                const wallConfigs = [
                    { name: 'wall-north', w: platformSize, h: wallHeight, d: wallThickness, pos: new Vector3(0, wallHeight / 2, platformSize / 2) },
                    { name: 'wall-south', w: platformSize, h: wallHeight, d: wallThickness, pos: new Vector3(0, wallHeight / 2, -platformSize / 2) },
                    { name: 'wall-east', w: wallThickness, h: wallHeight, d: platformSize, pos: new Vector3(platformSize / 2, wallHeight / 2, 0) },
                    { name: 'wall-west', w: wallThickness, h: wallHeight, d: platformSize, pos: new Vector3(-platformSize / 2, wallHeight / 2, 0) },
                ];
                wallConfigs.forEach(cfg => {
                    const wall = MeshBuilder.CreateBox(cfg.name, { width: cfg.w, height: cfg.h, depth: cfg.d }, scene);
                    wall.position = cfg.pos;
                    wall.isVisible = false; // invisible collider
                    wall.checkCollisions = true;
                    wall.physicsImpostor = new PhysicsImpostor(
                        wall,
                        PhysicsImpostor.BoxImpostor,
                        { mass: 0, restitution: 0, friction: 2.0 },
                        scene
                    );
                });

                console.log('Static physics impostors added to ground and walls.');
            }, 500);

            try {
                // Create WebXR with teleportation floor meshes
                const groundMesh = scene.getMeshByName("ground");

                xrInstance = await scene.createDefaultXRExperienceAsync({
                    floorMeshes: groundMesh ? [groundMesh] : [],
                });
                console.log("WebXRDefaultExperience is ready!", xrInstance);

                // Create VR Settings Menu (binds to left controller menu/Y button)
                vrMenuInstance = createVRMenu(scene, xrInstance, (newState) => {
                    console.log('VR Menu state changed:', newState);
                });
                if (vrMenuRef) vrMenuRef.current = vrMenuInstance;

                // Enable teleportation for VR locomotion
                if (xrInstance.baseExperience) {
                    try {
                        const teleportation = xrInstance.baseExperience.featuresManager.enableFeature(
                            WebXRFeatureName.TELEPORTATION,
                            "stable",
                            {
                                xrInput: xrInstance.input,
                                floorMeshes: groundMesh ? [groundMesh] : [],
                                snapPositions: [],
                            }
                        );
                        console.log("Teleportation enabled!", teleportation);
                    } catch (e) {
                        console.warn("Teleportation feature not available:", e);
                    }
                }

                // Setup VR controller grab mechanics
                if (xrInstance.input) {
                    xrInstance.input.onControllerAddedObservable.add((controller: any) => {
                        controller.onMotionControllerInitObservable.add((motionController: any) => {
                            // Get the trigger or squeeze component
                            const triggerComponent = motionController.getComponent("xr-standard-trigger");
                            const squeezeComponent = motionController.getComponent("xr-standard-squeeze");

                            // Squeeze = grab items
                            const grabComponent = squeezeComponent || triggerComponent;
                            if (grabComponent) {
                                grabComponent.onButtonStateChangedObservable.add(() => {
                                    if (grabComponent.pressed) {
                                        // Find nearest grabbable mesh to controller
                                        const controllerPos = controller.grip ?
                                            controller.grip.position :
                                            controller.pointer.position;

                                        if (!controllerPos) return;

                                        let nearestMesh: any = null;
                                        let nearestDist = 1.5; // grab range

                                        scene.meshes.forEach((mesh: any) => {
                                            if (Tags.MatchesQuery(mesh, "grabbable")) {
                                                // Always target the proxy mesh for interaction
                                                let target = mesh;
                                                while (target.parent && !target.name.startsWith("proxy-")) {
                                                    target = target.parent;
                                                }

                                                const dist = Vector3.Distance(controllerPos, target.getAbsolutePosition());
                                                if (dist < nearestDist) {
                                                    nearestDist = dist;
                                                    nearestMesh = target;
                                                }
                                            }
                                        });

                                        if (nearestMesh && !grabbedMeshRef.current) {
                                            grabbedMeshRef.current = nearestMesh;

                                            // FORCE WAKEUP if still static
                                            if (nearestMesh.metadata?.wakeUp) {
                                                nearestMesh.metadata.wakeUp();
                                            }

                                            if (nearestMesh.physicsImpostor) {
                                                nearestMesh.physicsImpostor.dispose();
                                                nearestMesh.physicsImpostor = null;
                                            }
                                            nearestMesh.setParent(controller.grip || controller.pointer);
                                            console.log(`Grabbed Proxy: ${nearestMesh.name}`);
                                        }
                                    } else {
                                        // Release with throw velocity
                                        if (grabbedMeshRef.current) {
                                            const releasedMesh = grabbedMeshRef.current;
                                            console.log(`Released: ${releasedMesh.name}`);
                                            const worldPos = releasedMesh.getAbsolutePosition().clone();
                                            releasedMesh.setParent(null);
                                            releasedMesh.position = worldPos;
                                            // Re-enable physics
                                            releasedMesh.physicsImpostor = new PhysicsImpostor(
                                                releasedMesh,
                                                PhysicsImpostor.BoxImpostor,
                                                { mass: 1, restitution: 0.1, friction: 0.9 },
                                                scene
                                            );
                                            // Add damping
                                            if (releasedMesh.physicsImpostor.physicsBody) {
                                                releasedMesh.physicsImpostor.physicsBody.angularDamping = 0.8;
                                                releasedMesh.physicsImpostor.physicsBody.linearDamping = 0.3;
                                            }
                                            // Apply a forward throw velocity from the controller
                                            const ctrlForward = (controller.pointer?.forward || new Vector3(0, 0, 1)).scale(3);
                                            releasedMesh.physicsImpostor.setLinearVelocity(ctrlForward);
                                            grabbedMeshRef.current = null;
                                        }
                                    }
                                });
                            }

                            // Trigger = interact with portal cube (ray-cast from controller)
                            if (triggerComponent && squeezeComponent) {
                                // Only add portal interaction on trigger if squeeze is separate
                                triggerComponent.onButtonStateChangedObservable.add(() => {
                                    if (triggerComponent.pressed && controller.pointer) {
                                        const ray = new Ray(
                                            controller.pointer.position,
                                            controller.pointer.forward,
                                            10
                                        );
                                        const hit = scene.pickWithRay(ray);
                                        if (hit?.pickedMesh?.name === "portal" && hit.distance < 5.0) {
                                            console.log(`VR Controller activated portal! Distance: ${hit.distance}`);
                                            cycleSkyboxRef.current();
                                            resetAllItems();
                                        }
                                    }
                                });
                            }
                        });
                    });
                }

            } catch (error) {
                console.error("Failed to create WebXRDefaultExperience:", error);
            }

            // Desktop grab/pickup with mouse
            let desktopGrabbedMesh: any = null;

            scene.onPointerObservable.add((pointerInfo) => {
                if (pointerInfo.type === PointerEventTypes.POINTERDOWN) {
                    if (pointerInfo.pickInfo?.hit && pointerInfo.pickInfo.pickedMesh) {
                        const mesh = pointerInfo.pickInfo.pickedMesh;
                        try {
                            if (Tags.MatchesQuery(mesh, "grabbable")) {
                                const camera = scene.activeCamera;
                                if (camera) {
                                    // Find its proxy parent if it's a child mesh
                                    let target: any = mesh;
                                    while (target.parent && !target.name.startsWith("proxy-")) {
                                        target = target.parent;
                                    }

                                    const dist = Vector3.Distance(camera.position, (target as any).getAbsolutePosition());
                                    if (dist < 8) {
                                        desktopGrabbedMesh = target as any;
                                        // Disable physics for smooth carrying
                                        if (desktopGrabbedMesh.physicsImpostor) {
                                            desktopGrabbedMesh.physicsImpostor.dispose();
                                            desktopGrabbedMesh.physicsImpostor = null;
                                        }
                                        desktopGrabbedMesh.setParent(camera);
                                        // Position it in front of camera
                                        desktopGrabbedMesh.position = new Vector3(0, -0.3, 2);
                                        console.log(`Desktop grabbed: ${desktopGrabbedMesh.name}`);
                                    }
                                }
                            } else if (mesh.name === 'portal') {
                                cycleSkyboxRef.current();
                                resetAllItems();
                            }
                        } catch {
                            // mesh may not have tags — check if it's the portal
                            if (mesh.name === 'portal') {
                                cycleSkyboxRef.current();
                                resetAllItems();
                            }
                        }
                    }
                }
                if (pointerInfo.type === PointerEventTypes.POINTERUP) {
                    if (desktopGrabbedMesh) {
                        const releasedMesh = desktopGrabbedMesh;
                        console.log(`Desktop released: ${releasedMesh.name}`);
                        const worldPos = releasedMesh.getAbsolutePosition().clone();
                        releasedMesh.setParent(null);
                        releasedMesh.position = worldPos;

                        // Re-enable physics with reset-proof params
                        releasedMesh.physicsImpostor = new PhysicsImpostor(
                            releasedMesh,
                            PhysicsImpostor.BoxImpostor,
                            { mass: 1, restitution: 0, friction: 2.0 },
                            scene
                        );
                        if (releasedMesh.physicsImpostor.physicsBody) {
                            releasedMesh.physicsImpostor.physicsBody.angularDamping = 0.99;
                            releasedMesh.physicsImpostor.physicsBody.linearDamping = 0.8;
                        }

                        // Throw forward
                        const cam = scene.activeCamera;
                        if (cam) {
                            const fwd = cam.getForwardRay().direction.scale(4);
                            releasedMesh.physicsImpostor.setLinearVelocity(fwd);
                        }
                        desktopGrabbedMesh = null;
                    }
                }
            });
        };

        init();

        return () => {
            if (vrMenuInstance) {
                vrMenuInstance.dispose();
            }
            if (xrInstance) {
                xrInstance.baseExperience?.exitXRAsync().then(() => {
                    xrInstance.dispose();
                }).catch((e: any) => console.error("Error disposing XR", e));
            }
        };
    }, [scene]);

    useEffect(() => {
        if (!scene) return;
        const observer = scene.onPointerObservable.add((pi) => {
            if (pi.type === PointerEventTypes.POINTERPICK && pi.pickInfo?.hit && pi.pickInfo.pickedMesh?.name === "portal") {
                const camera = scene.getCameraByName("camera1");
                if (camera && pi.pickInfo.pickedPoint) {
                    const distance = Vector3.Distance(camera.position, pi.pickInfo.pickedPoint);
                    console.log(`Portal picked! Distance: ${distance}`);
                    if (distance < 5.0) {
                        cycleSkybox();
                    } else {
                        console.log("Portal too far away to interact!");
                    }
                }
            }
        });

        // Floor Fall Protection Loop: If items fall below floor level, reset them
        const fallCheck = scene.onBeforeRenderObservable.add(() => {
            scene.meshes.forEach((mesh: any) => {
                if (mesh.name.startsWith("proxy-") && mesh.position.y < -3) {
                    console.log(`[Protection] Proxy ${mesh.name} fell! Resetting...`);
                    if (mesh.metadata?.originalPosition) {
                        mesh.setParent(null);
                        if (mesh.physicsImpostor) {
                            mesh.physicsImpostor.setLinearVelocity(Vector3.Zero());
                            mesh.physicsImpostor.setAngularVelocity(Vector3.Zero());
                            mesh.physicsImpostor.dispose();
                        }
                        mesh.position = mesh.metadata.originalPosition.clone();
                        mesh.physicsImpostor = new PhysicsImpostor(
                            mesh,
                            PhysicsImpostor.BoxImpostor,
                            { mass: 1, restitution: 0, friction: 2.0 },
                            scene
                        );
                        if (mesh.physicsImpostor.physicsBody) {
                            mesh.physicsImpostor.physicsBody.angularDamping = 0.99;
                            mesh.physicsImpostor.physicsBody.linearDamping = 0.99;
                        }
                    }
                }
            });
        });

        return () => {
            scene.onPointerObservable.remove(observer);
            scene.onBeforeRenderObservable.remove(fallCheck);
        };
    }, [scene, cycleSkybox]);

    return (
        <>
            <sphere name="skybox" diameter={1000} infiniteDistance={true} checkCollisions={false}>
                <standardMaterial name="skybox-mat" backFaceCulling={false} disableLighting={true}>
                    <texture key={skies[skyIndex]} url={skies[skyIndex]} assignTo="emissiveTexture" coordinatesMode={7} vScale={-1} wrapU={1} wrapV={1} />
                </standardMaterial>
            </sphere>
            <hemisphericLight name="light1" intensity={0.7} direction={Vector3.Up()} groundColor={new Color3(0.2, 0.2, 0.2)} />
            <pointLight name="spot" position={new Vector3(0, 3, 5)} intensity={0.8} shadowEnabled={true} diffuse={Color3.FromHexString("#00e6ff")} />
            <pointLight name="gallery-light-l" position={new Vector3(-4, 4, 3)} intensity={0.6} diffuse={Color3.FromHexString("#ffffff")} />
            <pointLight name="gallery-light-c" position={new Vector3(0, 4, 3)} intensity={0.6} diffuse={Color3.FromHexString("#ffffff")} />
            <pointLight name="gallery-light-r" position={new Vector3(4, 4, 3)} intensity={0.6} diffuse={Color3.FromHexString("#ffffff")} />

            <freeCamera
                name="camera1"
                position={new Vector3(0, 1.6, -5)}
                speed={0.15}
                applyGravity={true}
                checkCollisions={true}
                ellipsoid={new Vector3(0.5, 0.8, 0.5)}
                onCreated={(camera) => {
                    camera.setTarget(Vector3.Zero());
                    camera.keysUp.push(87); // W
                    camera.keysDown.push(83); // S
                    camera.keysLeft.push(65); // A
                    camera.keysRight.push(68); // D

                    camera.attachControl(true);

                    camera.getScene().onBeforeRenderObservable.add(() => {
                        if (camera.position.x > 9.5) camera.position.x = 9.5;
                        if (camera.position.x < -9.5) camera.position.x = -9.5;
                        if (camera.position.z > 9.5) camera.position.z = 9.5;
                        if (camera.position.z < -9.5) camera.position.z = -9.5;
                        if (camera.position.y < 1.0) camera.position.y = 1.0;
                    });
                }}
            />

            <ground name="ground" width={20} height={20} checkCollisions={true}>
                <pbrMaterial name="ground-mat"
                    metallic={0.5}
                    roughness={0.4}
                    albedoColor={new Color3(0.1, 0.1, 0.1)}
                >
                    <texture url="/assets/textures/floor.png" assignTo="albedoTexture" uScale={4} vScale={4} />
                    <texture url="/assets/textures/floor_bump.png" assignTo="bumpTexture" uScale={4} vScale={4} />
                </pbrMaterial>
            </ground>

            {/* Gallery item pedestals */}
            {GALLERY_ITEMS.map((item) => (
                <Pedestal key={`ped-${item.name}`} position={item.position} label={item.name} />
            ))}

            {/* Gallery items floating above pedestals */}
            {GALLERY_ITEMS.map((item) => (
                <GalleryItem key={`item-${item.name}`} item={item} />
            ))}

            <Room roomUrl={currentRoom} />
        </>
    );
};

const BabylonScene: React.FC = () => {
    const skies = [
        '/assets/textures/space.png',
        '/assets/textures/psychedelic.png',
        '/assets/textures/alien.png'
    ];
    const [skyIndex, setSkyIndex] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const vrMenuRef = useRef<any>(null);

    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const currentRoom = query.get('modelUrl') || '/assets/rooms/lobby.gltf';

    const cycleSkybox = () => {
        setSkyIndex(prev => (prev + 1) % skies.length);
    };

    const handleSoundToggle = () => {
        if (vrMenuRef.current) {
            // Also start audio if it hasn't been started yet (first user click)
            vrMenuRef.current.startAudio();
            const nowMuted = vrMenuRef.current.toggleMute();
            setIsMuted(nowMuted);
        }
    };

    return (
        <>
            <Engine antialias adaptToDeviceRatio canvasId="babylonJS">
                <Scene
                    collisionsEnabled={true}
                    gravity={new Vector3(0, -9.81, 0)}
                >
                    <SceneContent
                        skies={skies}
                        skyIndex={skyIndex}
                        cycleSkybox={cycleSkybox}
                        modelUrl={currentRoom}
                        vrMenuRef={vrMenuRef}
                    />
                </Scene>
            </Engine>

            {/* Sound mute/unmute overlay button — fixed position to avoid breaking canvas layout */}
            <button
                onClick={handleSoundToggle}
                title={isMuted ? 'Unmute Sound' : 'Mute Sound'}
                style={{
                    position: 'fixed',
                    bottom: '10px',
                    right: '80px',
                    width: '50px',
                    height: '50px',
                    background: 'rgba(51, 51, 51, 0.7)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    color: '#ffffff',
                    fontSize: '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 100,
                    transition: 'background 0.2s ease',
                    backdropFilter: 'blur(4px)',
                    pointerEvents: 'auto',
                }}
                onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(51, 51, 51, 0.9)';
                }}
                onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(51, 51, 51, 0.7)';
                }}
            >
                {isMuted ? '🔇' : '🔊'}
            </button>
        </>
    );
};

export default BabylonScene;
